import { Suspense, useEffect, useState } from 'react';
import { lazyLoad } from '@/utils/lazyLoad';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { loginSuccess } from '../../../redux/reducers/authReducer';
import { CardSkeleton } from '../../../shared/components/Skeleton/CardSkeleton';
import { MainLoading } from '../../../shared/components/Skeleton/MainLoading';
import { getFromSessionStorage, removeFromSessionStorage, saveToSessionStorage } from '../../../utils/SessionStorage';
import { clearCookies } from '../../../utils/cookies';
import useTabCounter from '../../../utils/hooks/useTabCounter';
import privateRoutes from './PrivateRoutes/Routes';
// import superAdminRoutes from './PrivateRoutes/SuperAdminRoutes';
import useUrlQueryParams from '@/utils/hooks/useUrlQueryParams';
import * as Imports from './mainFileImports';

const NotFound404 = lazyLoad(() => import('../../Account/404'));
const LockScreen = lazyLoad(() => import('../../Account/LockScreen/index'));
const ResetPassword = lazyLoad(() => import('../../Account/ResetPasswordPage'));
const Reset2FA = lazyLoad(() => import('../../Account/Reset2FA'));
const CreatePassword = lazyLoad(() => import('../../Account/CreatePassword'));
const SignInWithSSO = lazyLoad(() => import('../../Account/SignInWithSSO'));
const SSOToken = lazyLoad(() => import('../../Account/SignInWithSSO/ssoToken'));
const Verify2fa = lazyLoad(() => import('../../Auth/2FA/index'));
const Login = lazyLoad(() => import('../../Auth/Login/index'));

let routes;

// Add this new component to simulate loading
const DelayedComponent = ({ children, isLoggedIn }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate a 2-second loading delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return isLoading ? isLoggedIn ? <MainLoading /> : <CardSkeleton /> : children;
};

function Router() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { encryptQueryParams } = useUrlQueryParams();

  const {
    MainWrapper,
    SimpleLoader,
    LoadingWrap,
    MainContainerWrapper,
    ProtectedRoutes,
    setAllPermissions,
    setModulePermissions,
    setScopePermissions,
    resetAllModulePermissions,
    setAllowedModulePermissions,
    setPermissionArray,
    setCustomerName,
    getLocalStorageData,
    setLocalStorageData,
    userGuideObj,
    staticPermissions,
    filterAllowedRoutes,
    generateModulePermissions,
    authRedirect,
    updateDecodedPermissions,
    MemoizedSidebar,
    setUserGuideToggle,
  } = Imports;

  const isLoggedIn = useSelector((state) => state?.auth?.isLoggedIn);
  const currentUser = useSelector((state) => state?.auth?.currentUser);
  const customerOnboarded = useSelector((state) => state?.auth?.currentUser?.customerOnboarded);
  const customerOnboardingFormsCompleted = useSelector(
    (state) => state?.auth?.currentUser?.customerOnboardingFormsCompleted,
  );
  const customerName = useSelector((state) => state?.auth?.currentUser?.customerName);
  const customerID = useSelector((state) => state?.auth?.currentUser?.customerID);
  const userID = useSelector((state) => state?.auth?.currentUser?.userID);
  console.log('customerOnboardingFormsCompleted', customerOnboardingFormsCompleted);
  const redirectUrl = useSelector((state) => state?.auth?.currentUser?.redirectUrl);

  const [permissionEffect, setPermissionEffect] = useState(currentUser?.permissions);
  const currentUserCopy = { ...currentUser };
  const { username } = currentUser || {};
  const [isLoggedInLoading, setIsLoggedInLoading] = useState(true);
  const currentCustomerID = useSelector((state) => state?.customer?.currentCustomer?.customerID);
  const siteID = useSelector((state) => state?.site?.currentSite?.key);
  const currentUserToken = useSelector((state) => state?.auth?.currentUser?.token?.token);
  const modulePermissions = useSelector((state) => state?.permissionsStorage?.modulePermissions);

  console.log('modulePermissions', modulePermissions);
  const [allAllowedRoutes, setAllAllowedRoutes] = useState([]);

  const token = (() => {
    try {
      const tokenData = getLocalStorageData('token');
      return tokenData ? JSON.parse(tokenData)?.currentUser?.token : null;
    } catch (error) {
      console.error('Error parsing token from getLocalStorageData:', error);
      return null;
    }
  })();

  const [hasRedirectedToActionItems, setHasRedirectedToActionItems] = useState(() => {
    return getLocalStorageData('hasRedirectedToActionItems') === 'true';
  });

  useTabCounter();

  useEffect(() => {
    const publicRoutes = [
      '/login',
      '/lockScreen',
      '/resetPassword',
      '/createPassword',
      '/loginSSO',
      '/2fa',
      '/reset2FA',
    ];
    const clearRoutes = ['/resetPassword', '/createPassword'];
    if (clearRoutes.some((route) => location.pathname.toLowerCase().startsWith(route.toLowerCase()))) {
      clearCookies();
      sessionStorage.clear();
      localStorage.clear();
    }
    if (
      !isLoggedIn &&
      !publicRoutes.includes(location.pathname) &&
      !['sso', 'token', 'login'].some((name) => !location.pathname.includes(name))
    ) {
      saveToSessionStorage('userRedirectUrl', location.pathname);
    } else if (isLoggedIn) {
      const redirectUrl = getFromSessionStorage('userRedirectUrl');
      if (redirectUrl) {
        navigate(redirectUrl);
        removeFromSessionStorage('userRedirectUrl');
      }
    }
  }, [isLoggedIn, location.pathname, navigate]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'token') {
        try {
          const tokenString = localStorage.getItem('token');
          if (tokenString && tokenString !== 'null' && tokenString !== 'undefined') {
            const token = JSON.parse(tokenString);
            if (token?.currentUser) {
              if (isLoggedIn === true) {
                dispatch(loginSuccess(token?.currentUser));
              }
            }
          }
        } catch (error) {
          console.error('Error parsing token from localStorage:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && !getLocalStorageData('logoutTime') && !getLocalStorageData('loginTime')) {
      const loginTime = new Date().getTime();
      const expirationTime = loginTime + 8 * 60 * 60 * 1000;
      setLocalStorageData('logoutTime', expirationTime);
      setLocalStorageData('loginTime', loginTime);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && location.pathname !== '/documents/files' && getLocalStorageData('breadcrumb')) {
      localStorage.removeItem('breadcrumb');
    }
  }, [isLoggedIn, location.pathname]);

  useEffect(() => {
    setIsLoggedInLoading(isLoggedIn);
  }, [isLoggedIn, location.pathname]);

  useEffect(() => {
    authRedirect(token, isLoggedIn, location, navigate, username, dispatch);
  }, [isLoggedIn, location.pathname]);

  useEffect(() => {
    if (window.location?.search.includes('source=redirectedFromDocsSite')) {
      const currentRoute = location.pathname.replace(/^\/|\/$/g, '');
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab');

      dispatch(setUserGuideToggle());
      if (isLoggedIn && userGuideObj[currentRoute]) {
        const dataObj = userGuideObj[currentRoute];

        // If tab parameter exists and matches one of the available guides, use that specific guide
        const guideToRun =
          tabParam && dataObj[tabParam] ? dataObj[tabParam](dispatch, navigate) : dataObj(dispatch, navigate);

        if (!window.location?.search.includes('source=redirectedFromDocsSite')) {
          console.log('The user either came directly or from the same site');
        } else {
          console.log('The user came from a different website');
          setTimeout(() => {
            guideToRun.drive();
          }, 3500);
        }
      }
    }
  }, [location]);

  useEffect(() => {
    if (
      (token &&
        currentUser?.permissionBits &&
        allAllowedRoutes.length === 0 &&
        currentUser?.permissions &&
        location.pathname !== '/login') ||
      allAllowedRoutes.length !== 0
    ) {
      if (currentUser?.permissionBits) {
        const arrayGenerated = Object.keys(currentUser?.permissionBits).map(
          (key) => currentUserCopy?.permissionBits[key],
        );
        dispatch(setPermissionArray(arrayGenerated));

        if (currentUserCopy?.customerName) {
          dispatch(setCustomerName(currentUserCopy.customerName));
        }
        currentUserCopy.permissions = { ...currentUserCopy.permissions };
        // currentUserCopy.permissions = currentUserCopy.permissions || {}
        for (const key in staticPermissions) {
          if (!currentUserCopy.permissions[key]) {
            currentUserCopy.permissions[key] = staticPermissions[key];
          }
        }

        dispatch(setAllPermissions(currentUserCopy.permissions));

        if (Object.keys(currentUserCopy.permissions).length) {
          updateDecodedPermissions(currentUserCopy, {
            customers: ['company', 'companymanagement'],
            tickets: ['alltickets'],
            projects: ['allprojects'],
            permissions: ['permissions_roles', 'permissions_teams'],
            inventory: ['inventory_dashboard', 'inventory_management'],
            onboarding: ['onboarding_questionnaire', 'onboarding_templates'],
          });

          const firstCharacters = {};
          for (const key in currentUserCopy.permissions) {
            firstCharacters[key] = currentUserCopy.permissions[key][0];
          }

          dispatch(setModulePermissions(firstCharacters));
          dispatch(
            setScopePermissions({
              isStnUser: currentUserCopy.isStnUser,
              isCompanyUser: currentUserCopy.isCompanyUser,
            }),
          );

          dispatch(resetAllModulePermissions());
          if (arrayGenerated) {
            Object.keys(currentUserCopy.permissions).forEach((module) => {
              generateModulePermissions(currentUserCopy.permissions, arrayGenerated, module, dispatch);
            });
          }
        }
      }

      const codeParam = new URLSearchParams(location.search).get('code');
      if (codeParam) {
        setLocalStorageData('stn_ninja_code', codeParam);
      }
    }
  }, [
    location.pathname,
    currentCustomerID,
    siteID,
    currentUserToken,
    currentUser?.permissions,
    currentUser?.permissionBits,
    permissionEffect,
  ]);

  useEffect(() => {
    routes = privateRoutes;
  }, [currentCustomerID, siteID]);

  useEffect(() => {
    if (isLoggedIn && token) {
      filterAllowedRoutes(routes, modulePermissions, dispatch, setAllAllowedRoutes, setAllowedModulePermissions);
    }
  }, [modulePermissions, currentCustomerID, siteID, currentUserToken]);

  useEffect(() => {
    // Only proceed if we have a valid customerOnboarded state
    if (customerOnboarded === undefined) return;

    // Get the current path without trailing slash for comparison
    const currentPath = location.pathname.replace(/\/$/, '');

    if (customerOnboarded === false && currentPath !== '/onboarding_system') {
      navigate('/onboarding_system');
    } else if (customerOnboarded === true) {
      if (
        customerOnboardingFormsCompleted === false &&
        !currentPath.includes('/company/') &&
        !hasRedirectedToActionItems
      ) {
        const encryptedParams = encryptQueryParams({ customerID, userID, subModule: 'onboarding_action_items' });
        console.log('encryptedParams_company', encryptedParams);
        setHasRedirectedToActionItems(true);
        setLocalStorageData('hasRedirectedToActionItems', 'true');
        navigate(`/company/${customerName}?${encryptedParams}`);
      } else if (customerOnboarded === true && currentPath === '/onboarding_system') {
        if (redirectUrl) {
          navigate(redirectUrl);
        } else if (modulePermissions && Object.keys(modulePermissions).length > 0) {
          const redirectPath = Object.keys(modulePermissions)[0];
          navigate(`/${redirectPath}`);
        }
      }
    }
  }, [
    customerOnboarded,
    customerOnboardingFormsCompleted,
    navigate,
    location.pathname,
    redirectUrl,
    modulePermissions,
    customerName,
    customerID,
    encryptQueryParams,
    hasRedirectedToActionItems,
  ]);

  // Add cleanup effect to remove the flag when logging out
  useEffect(() => {
    if (!isLoggedIn) {
      setHasRedirectedToActionItems(false);
      localStorage.removeItem('hasRedirectedToActionItems');
    }
  }, [isLoggedIn]);

  return (
    <MainWrapper>
      <main>
        <Suspense fallback={isLoggedIn ? <MainLoading /> : <CardSkeleton />}>
          {/* <DelayedComponent isLoggedIn={isLoggedIn}> */}
          {isLoggedInLoading && token && <MemoizedSidebar breadcrumbs={Imports.breadcrumbs} />}
          <Routes>
            <Route
              exact
              path='/login'
              element={<Login />}
            />
            <Route
              exact
              path='/lockScreen'
              element={<LockScreen />}
            />
            <Route
              path='/resetPassword'
              element={<ResetPassword />}
            />
            <Route
              path='/createPassword'
              element={<CreatePassword />}
            />
            <Route
              exact
              path='/loginSSO'
              element={<SignInWithSSO />}
            />
            <Route
              path='/sso/token'
              element={<SSOToken />}
            />
            <Route
              exact
              path='/2fa'
              element={<Verify2fa />}
            />
            <Route
              path='/reset2FA'
              element={<Reset2FA />}
            />
            {token && isLoggedInLoading && allAllowedRoutes.length > 0 && (
              <Route
                path='/'
                element={<MainContainerWrapper />}
              >
                {allAllowedRoutes.map(({ path, component }) => (
                  <Route
                    key={path}
                    exact
                    path={path}
                    element={<ProtectedRoutes>{component}</ProtectedRoutes>}
                  />
                ))}
              </Route>
            )}
            <Route
              path='*'
              element={<NotFound404 />}
            />
          </Routes>
          {/* </DelayedComponent> */}
        </Suspense>
      </main>
    </MainWrapper>
  );
}

export default Router;
